import { useChatContext } from "@/features/workstation/hooks/useChatContext";
import { useState } from "react";
import type { FeedbackType, PersistedFeedbackStruct } from '@/api/types/workstation.types';

interface UseFeedbackMessageOptions {
  messageId: string;
  onFeedbackSubmit: (
    type: FeedbackType,
    messageId: string,
    operation: "add" | "remove",
    reason?: string,
    suggestions?: string[]
  ) => void;
  setShowThanksForFeedback: (value: boolean) => void;
}

export function useFeedbackMessage({
  messageId,
  onFeedbackSubmit,
  setShowThanksForFeedback,
}: UseFeedbackMessageOptions) {
  const { feedbackHandlers } = useChatContext();
  const feedbackState = feedbackHandlers.getInMemoryFeedbackMessage(messageId);
  const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false);

  const handlePositive = () => {
    const isRemoving = feedbackState?.feedbackType === "positive";
    const operation = isRemoving ? "remove" : "add";
    onFeedbackSubmit("positive", messageId, operation);

    if (isRemoving) {
      feedbackHandlers.removeInMemoryFeedbackMessage(messageId);
    } else {
      const feedback: PersistedFeedbackStruct = {
        feedbackType: "positive",
        reason: "",
        suggestedReasons: [],
      };
      feedbackHandlers.addInMemoryFeedbackMessage(messageId, feedback);
      setShowThanksForFeedback(true);
    }
  };

  const handleNegative = () => {
    const isRemoving = feedbackState?.feedbackType === "negative";
    if (isRemoving) {
      onFeedbackSubmit("negative", messageId, "remove");
      feedbackHandlers.removeInMemoryFeedbackMessage(messageId);
    } else {
      setShowFeedbackModal(true);
    }
  };

  const handleSubmitNegative = ({
    reason,
    suggestions = [],
  }: {
    reason?: string;
    suggestions: string[];
  }) => {
    setShowFeedbackModal(false);
    onFeedbackSubmit("negative", messageId, "add", reason?.trim(), suggestions);
    feedbackHandlers.addInMemoryFeedbackMessage(messageId, {
      feedbackType: "negative",
      reason,
      suggestedReasons: suggestions,
    });
    setShowThanksForFeedback(true);
  };

  return {
    feedbackState,
    showFeedbackModal,
    setShowFeedbackModal,
    handlePositive,
    handleNegative,
    handleSubmitNegative,
  };
}
